import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import {
  PAGE_SIZES,
  type DriverBilling,
  type LedgerStatusFilter,
  type PaymentMethod,
  type RecoveryFilters,
  type RecoveryListResponse,
} from './recovery.types'

/** Lit les filtres bruts de l'URL et applique les valeurs par défaut. */
export function parseFilters(search: Record<string, unknown>): RecoveryFilters {
  const page = Number(search.page)
  const limit = Number(search.limit)
  const status =
    search.status === 'ACTIVE' || search.status === 'SUSPENDED' ? search.status : undefined
  return {
    status: status as LedgerStatusFilter | undefined,
    overdue: search.overdue === 'true' || search.overdue === true || undefined,
    page: Number.isFinite(page) && page >= 1 ? page : 1,
    limit: (PAGE_SIZES as readonly number[]).includes(limit) ? limit : 25,
  }
}

/** Filtres → params HTTP, en omettant les valeurs vides. */
export function toParams(filters: RecoveryFilters): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {
    page: filters.page,
    limit: filters.limit,
  }
  if (filters.status) params.status = filters.status
  if (filters.overdue) params.overdue = true
  return params
}

/** Liste paginée du recouvrement. `keepPreviousData` évite le flash entre pages. */
export function useRecoveryDrivers(filters: RecoveryFilters) {
  return useQuery<RecoveryListResponse>({
    queryKey: ['admin', 'recovery', filters],
    queryFn: async () => {
      const { data } = await api.get<RecoveryListResponse>('/admin/billing/drivers', {
        params: toParams(filters),
      })
      return data
    },
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  })
}

/** Fiche recouvrement d'un chauffeur : factures + paiements. */
export function useDriverBilling(driverId: string | null) {
  return useQuery<DriverBilling>({
    queryKey: ['admin', 'recovery', 'driver', driverId],
    queryFn: async () => {
      const { data } = await api.get<DriverBilling>(`/admin/billing/drivers/${driverId}`)
      return data
    },
    enabled: !!driverId,
    staleTime: 10_000,
  })
}

/** Extrait le filename de l'en-tête Content-Disposition, sinon repli daté. */
function filenameFromHeader(header: unknown): string {
  if (typeof header === 'string') {
    const match = header.match(/filename="?([^"]+)"?/)
    if (match) return match[1]
  }
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, '')
  return `recouvrement_${date}.csv`
}

/**
 * Télécharge l'export CSV (toutes les lignes filtrées, pagination ignorée). Le CSV
 * est généré côté serveur ; on ne fait que déclencher le download du blob.
 */
export function useExportRecovery() {
  return useMutation({
    mutationFn: async (filters: RecoveryFilters) => {
      const exportParams = toParams(filters)
      delete exportParams.page
      delete exportParams.limit
      const response = await api.get('/admin/billing/export', {
        params: exportParams,
        responseType: 'blob',
      })

      const filename = filenameFromHeader(response.headers['content-disposition'])
      const url = URL.createObjectURL(response.data as Blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    },
  })
}

export interface RecordPaymentInput {
  driverId: string
  amount: number
  method: PaymentMethod
  reference?: string
  proofFile?: File | null
}

/**
 * Enregistre un paiement (SCRUM-95). Si un justificatif est fourni : demande une URL
 * presignée, téléverse le fichier directement sur S3 (sans en-tête Authorization,
 * sinon la signature S3 est invalide), puis crée le paiement avec la clé S3.
 */
export function useRecordPayment(driverId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: RecordPaymentInput) => {
      let proofS3Key: string | undefined
      if (input.proofFile) {
        const { data: presign } = await api.post('/admin/billing/payments/proof-upload-url', {
          driverId: input.driverId,
          contentType: input.proofFile.type,
        })
        await fetch(presign.presignedUrl, {
          method: 'PUT',
          body: input.proofFile,
          headers: { 'Content-Type': input.proofFile.type },
        })
        proofS3Key = presign.s3Key
      }

      const { data } = await api.post('/admin/billing/payments', {
        driverId: input.driverId,
        amount: input.amount,
        method: input.method,
        reference: input.reference || undefined,
        proofS3Key,
      })
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'recovery', 'driver', driverId] })
      qc.invalidateQueries({ queryKey: ['admin', 'recovery'] })
    },
  })
}
